/**
 * ActualInterviewPage - LiveKit Integration
 * 
 * This page handles the live interview session using LiveKit for real-time streaming
 * and server-side recording via LiveKit Egress.
 * 
 * Key features:
 * - LiveKit room connection for real-time video/audio streaming
 * - Server-side recording via LiveKit Egress (per question)
 * - Camera + audio and screen share tracks
 * - Integration with existing InterviewQuestions component
 * - Navigation protection during active interview
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loading } from '../components/Loading';
import { endInterview, startInterview, getInterviewByToken, getInterviewDuration, getInterviewResumeState, getLiveKitToken, startQuestionRecording, stopQuestionRecording, getPsychometricSections, logInterviewQuestionsEnd } from '../services/interview.service';
import i18n from '../i18n/index';
import { TENANT_LOCALE_STORAGE_KEY } from '../i18n/config';
import { normalizeLocale } from '@/utils/locale';
import { toast } from 'sonner';
import { Mic, MicOff, Camera, CameraOff, Monitor, MonitorOff, Chrome, Check, X, AlertTriangle } from 'lucide-react';
import { useAzureTTS } from '../hooks/useAzureTTS';
import { RoomContext, ParticipantTile, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useLiveKitInterview } from '../hooks/useLiveKitInterview';
import { useRealtimeTranscription } from '../hooks/useRealtimeTranscription';
import { useCopyProtection } from '../hooks/useCopyProtection';
import { REALTIME_AZURE_FALLBACK_ENABLED } from '../constants/featureFlags';
import {
  InterviewQuestions,
  ExitInterviewConfirmModal,
  InterviewProgressStepper,
  VoiceDisplay,
  PsychometricTest,
} from '../components/interview';
import { InterviewStatus } from '../models/interview';
import { QuestionItem, PsychometricSectionSnapshot } from '../types/interview.types';
import { DEFAULT_EXAM_LANGUAGE, type ExamLanguage } from '../types/exam-config.types';
import { normalizeExamLanguage } from '../utils/locale';
import { Button } from '../components/ui/atoms/Button';
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';
import {
  endOfQuestionsAction,
  endOfQuestionsButtonId,
  endOfQuestionsButtonLabelKey,
} from '../utils/interviewPhase';


// Use Sonner toast for notifications
const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
  console.log(`[Notification] ${type.toUpperCase()}: ${message}`);
  
  if (type === 'error') {
    toast.error(message);
  } else if (type === 'success') {
    toast.success(message);
  } else {
    toast.info(message);
  }
};

// Component for LiveKit Participant Tiles - Horizontal Layout
const ParticipantTilesHorizontalComponent = ({ isVoiceDetected }: { isVoiceDetected: boolean }) => {
  const { t } = useTranslation('interview');
  const cameraTracks = useTracks([Track.Source.Camera]);
  const screenTracks = useTracks([Track.Source.ScreenShare]);

  return (
    <div className="flex gap-4 h-full">
      {/* Video Feed Tile */}
      <div className="flex-1 bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gradient-1 py-1 px-2">
          <h2 className="text-dark-invirtus text-xs font-bold truncate font-heading">📹 {t('live.feed.video')}</h2>
        </div>
        <div className="relative bg-gray-900" style={{ height: 'calc(100% - 28px)' }}>
          {cameraTracks.length > 0 ? (
            <ParticipantTile 
              trackRef={cameraTracks[0]}
              className="w-full h-full"
              disableSpeakingIndicator={false}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white text-xs">
              {t('live.feed.no_camera')}
            </div>
          )}
        </div>
      </div>

      {/* Screen Share Tile */}
      <div className="flex-1 bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gradient-1 py-1 px-2">
          <h2 className="text-dark-invirtus text-xs font-bold truncate font-heading">🖥️ {t('live.feed.screen')}</h2>
        </div>
        <div className="relative bg-gray-900" style={{ height: 'calc(100% - 28px)' }}>
          {screenTracks.length > 0 ? (
            <ParticipantTile 
              trackRef={screenTracks[0]}
              className="w-full h-full"
              disableSpeakingIndicator={true}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white text-xs">
              {t('live.feed.no_screen')}
            </div>
          )}
        </div>
      </div>

      {/* Audio Visualization Tile */}
      <div className="flex-1">
        <VoiceDisplay
          isEnabled={true}
          isVoiceDetected={isVoiceDetected}
          className="h-full w-full"
        />
      </div>
    </div>
  );
};

export const ActualInterviewPage = () => {
  const { t } = useTranslation('interview');
  const { interviewId } = useParams<{ interviewId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const notify = useCallback(
    (
      key: `live.notifications.${string}`,
      type: 'success' | 'error' | 'info',
      options?: Record<string, string>
    ) => {
      showNotification(t(key as never, options), type);
    },
    [t]
  );
  
  // Component State
  const [isEnding, setIsEnding] = useState<boolean>(false);
  const [initialStateCheckDone, setInitialStateCheckDone] = useState(false);
  const [isInterviewStarted, setIsInterviewStarted] = useState<boolean>(false);
  const [isStartingInterview, setIsStartingInterview] = useState<boolean>(false);
  const [showNavigationConfirm, setShowNavigationConfirm] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<boolean>(false);
  const [exitConfirmationEnabled] = useState<boolean>(true);
  const [showEndConfirm, setShowEndConfirm] = useState<boolean>(false);
  
  // LiveKit Setup State
  const [setupComplete, setSetupComplete] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [preGrantedStreams, setPreGrantedStreams] = useState<{
    cameraStream: MediaStream | null;
    screenStream: MediaStream | null;
  } | null>(null);
  const [livekitCredentials, setLivekitCredentials] = useState<{
    roomName: string;
    token: string;
    url: string;
  } | null>(null);
  
  // State reflecting the dynamic interview properties
  const [currentStatus, setCurrentStatus] = useState<InterviewStatus | null>(null);
  const [currentStartedAt, setCurrentStartedAt] = useState<number | null>(null);
  const [currentTimeRemaining, setCurrentTimeRemaining] = useState<number | null>(null);

  // Ref for tracking current question
  const currentQuestionIdRef = useRef<string | null>(null);
  
  // Ref for InterviewQuestions to trigger next question
  const nextQuestionTriggerRef = useRef<(() => void) | null>(null);

  // State for progress tracking
  const [currentQuestion, setCurrentQuestion] = useState<QuestionItem | null>(null);
  const [totalQuestions, setTotalQuestions] = useState<number>(0);
  const [allQuestions, setAllQuestions] = useState<QuestionItem[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(-1);
  
  // Interview phase: interview questions -> psychometric test -> complete
  const [interviewPhase, setInterviewPhase] = useState<'interview' | 'psychometric' | 'complete'>('interview');
  const [psychometricSections, setPsychometricSections] = useState<PsychometricSectionSnapshot[]>([]);

  // Interview language for TTS (from startInterview response)
  const [interviewLanguage, setInterviewLanguage] = useState<ExamLanguage>(DEFAULT_EXAM_LANGUAGE);
  const [workspaceLogoUrl, setWorkspaceLogoUrl] = useState<string | null>(null);
  const isCustomBrandLogo = Boolean(workspaceLogoUrl);
  const brandLogoSrc = workspaceLogoUrl || '/logo/dark-logo.png';

  // Azure TTS hook — initialized once, survives question transitions
  const { speak, stop: stopTTS, isPlaying: isTTSPlaying, isMuted, setIsMuted, ttsError } = useAzureTTS({
    interviewId: interviewId || '',
    language: interviewLanguage,
    apiBaseUrl: import.meta.env.VITE_API_SERVER_URL || '',
  });

  // Voice Activity Detection
  const [isVoiceDetected, setIsVoiceDetected] = useState(false);
  const [isAudioVerified, setIsAudioVerified] = useState(false); // Track if audio has been verified (voice detected at least once)
  const audioAnalyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const voiceDetectionRafRef = useRef<number | null>(null);

  // Chrome browser detection
  const isChromeBrowser = /Chrome/.test(navigator.userAgent) && !/Edg|OPR/.test(navigator.userAgent);

  // Anti-cheating: Prevent copying of interview questions
  const {
    onCopy: handleCopyProtection,
    onCut: handleCutProtection,
    onContextMenu: handleContextMenuProtection,
    protectedStyle: copyProtectedStyle,
  } = useCopyProtection({
    enabled: isInterviewStarted,
    showWarning: true,
    warningMessage: t('live.copy_warning'),
  });

  // Read initial state from navigation
  const initialState = location.state as {
    timeRemainingMinutes: number;
    startedAt: number;
    interviewStatus: InterviewStatus;
    talentName?: string;
    jobTitle?: string;
    invitationToken?: string;
    locale?: string;
    needsMediaSetup?: boolean;
    room_name?: string;
    livekit_url?: string;
    livekit_token?: string;
    workspaceLogoUrl?: string | null;
  } | null;

  useEffect(() => {
    setWorkspaceLogoUrl(initialState?.workspaceLogoUrl ?? null);
  }, [initialState?.workspaceLogoUrl]);

  useEffect(() => {
    const rawLocale = initialState?.locale;
    if (!rawLocale) return;
    void i18n.changeLanguage(normalizeLocale(rawLocale));
    return () => {
      const tenantLocale =
        localStorage.getItem(TENANT_LOCALE_STORAGE_KEY) || 'en';
      void i18n.changeLanguage(tenantLocale);
    };
  }, [initialState?.locale]);

  useEffect(() => {
    if (!initialState?.invitationToken) return;

    let cancelled = false;
    void getInterviewByToken(initialState.invitationToken).then((response) => {
      if (cancelled) return;

      setWorkspaceLogoUrl(response.data?.workspace_logo_url ?? null);

      if (initialState?.locale) return;
      if (!response.data?.locale) return;
      const normalizedLocale = normalizeLocale(response.data.locale);
      navigate(location.pathname, {
        replace: true,
        state: { ...initialState, locale: normalizedLocale },
      });
    });

    return () => {
      cancelled = true;
    };
  }, [initialState?.invitationToken, initialState?.locale, location.pathname, navigate]);

  // Voice Activity Detection helpers (defined early to avoid reference errors)
  const stopVoiceDetection = useCallback(() => {
    if (voiceDetectionRafRef.current) {
      cancelAnimationFrame(voiceDetectionRafRef.current);
      voiceDetectionRafRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    audioAnalyserRef.current = null;
    setIsVoiceDetected(false);
  }, []);

  const startVoiceDetection = useCallback((stream: MediaStream) => {
    stopVoiceDetection();
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      audioAnalyserRef.current = analyser;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      function detect() {
        analyser.getByteTimeDomainData(dataArray);
        const max = Math.max(...dataArray);
        const min = Math.min(...dataArray);
        const amplitude = max - min;
        const detected = amplitude > 20; // Threshold
        setIsVoiceDetected(detected);
        // Mark audio as verified once voice is detected
        if (detected) {
          setIsAudioVerified(true);
        }
        voiceDetectionRafRef.current = requestAnimationFrame(detect);
      }
      detect();
    } catch (e) {
      console.error('[ActualInterviewPage] Error starting voice detection:', e);
      setIsVoiceDetected(false);
    }
  }, [stopVoiceDetection]);


  // LiveKit Integration
  const {
    room,
    cameraVideoRef,
    screenVideoRef,
    connectToRoom,
    disconnect,
  } = useLiveKitInterview({
    roomName: livekitCredentials?.roomName,
    token: livekitCredentials?.token,
    livekitUrl: livekitCredentials?.url,
    autoConnect: false, // We'll connect manually after getting permissions
  });

  // Realtime Azure STT fallback. Taps the candidate's mic from the
  // pre-granted stream (the same one the page publishes to LiveKit),
  // streams 16 kHz PCM to our backend. The post-hoc Azure transcript
  // remains primary; this fills in per-question gaps when the LiveKit
  // recording was lost or empty.
  const fallbackAudioTrack = useMemo(
    () => preGrantedStreams?.cameraStream?.getAudioTracks()[0] ?? null,
    [preGrantedStreams?.cameraStream],
  );
  const realtimeTranscription = useRealtimeTranscription(
    fallbackAudioTrack,
    interviewId,
    REALTIME_AZURE_FALLBACK_ENABLED,
  );


  // Handle navigation confirmation when interview is in progress
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (isInterviewStarted && !isEnding) {
        event.preventDefault();
        setShowNavigationConfirm(true);
        window.history.pushState(null, '', window.location.href);
        return;
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isInterviewStarted && !isEnding) {
        setShowNavigationConfirm(true);
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isInterviewStarted && !isEnding) {
        const isCtrlW = (event.ctrlKey || event.metaKey) && event.key === 'w';
        const isCtrlShiftW = (event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'w';
        const isCtrlF4 = (event.ctrlKey || event.metaKey) && event.key === 'F4';
        const isAltF4 = event.altKey && event.key === 'F4';
        
        if (isCtrlW || isCtrlShiftW || isCtrlF4 || isAltF4) {
          event.preventDefault();
          setShowNavigationConfirm(true);
          return false;
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('keydown', handleKeyDown);
    
    if (isInterviewStarted && !isEnding) {
      window.history.pushState(null, '', window.location.href);
    }
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isInterviewStarted, isEnding, showNavigationConfirm, exitConfirmationEnabled]);

  // Initial setup effect
  useEffect(() => {
    console.log('[ActualInterviewPage] Mounted. Interview ID:', interviewId);
    console.log('[ActualInterviewPage] Received initial state:', initialState);

    if (initialState) {
      setCurrentStatus(initialState.interviewStatus);
      setCurrentStartedAt(initialState.startedAt);
      setCurrentTimeRemaining(initialState.timeRemainingMinutes);

      const alreadyInProgress = initialState.interviewStatus === InterviewStatus.IN_PROGRESS && !initialState.needsMediaSetup;
      setIsInterviewStarted(alreadyInProgress); 
      console.log(`[ActualInterviewPage] Initial Status: ${initialState.interviewStatus}, Needs Setup: ${initialState.needsMediaSetup}, Setting isInterviewStarted to: ${alreadyInProgress}`);
    } else {
      console.warn('[ActualInterviewPage] Initial state missing during setup.');
    }
    
    return () => {
      console.log('[ActualInterviewPage] Unmounting.');
    };
  }, [interviewId]);

  // Fetch interview duration
  useEffect(() => {
    if (interviewId && initialStateCheckDone) {
      const fetchDuration = async () => {
        try {
          const response = await getInterviewDuration(interviewId);
          if (response.data) {
            console.log('[ActualInterviewPage] Interview duration fetched:', response.data.duration_minutes);
          } else if (response.error) {
            console.error('[ActualInterviewPage] Error fetching interview duration:', response.error);
          }
        } catch (error) {
          console.error('[ActualInterviewPage] Exception fetching interview duration:', error);
        }
      };
      
      fetchDuration();
    }
  }, [interviewId, initialStateCheckDone]);

  // Restores initial state after a hard reload.
  //
  // The prep page hands location.state to this page on first navigation,
  // but a refresh / browser close / network drop wipes it. When that
  // happens we call /resume-state on the backend, rebuild the same
  // shape from the response, and re-navigate with it so the rest of the
  // page (which reads `initialState`) just works. The candidate then
  // goes through media setup again and resumes from the current question.
  useEffect(() => {
    if (initialStateCheckDone) return;
    if (!location.pathname.includes('/actual-interview/')) return;

    const haveAllRequiredFields =
      !!initialState &&
      typeof initialState.timeRemainingMinutes === 'number' &&
      !!initialState.startedAt &&
      !!initialState.interviewStatus;

    if (haveAllRequiredFields) {
      setInitialStateCheckDone(true);
      return;
    }

    if (!interviewId) {
      navigate('/', { replace: true, state: { error: 'Missing interview id' } });
      return;
    }

    let cancelled = false;
    (async () => {
      console.log('[ActualInterviewPage] location.state missing — fetching resume-state from backend');
      const response = await getInterviewResumeState(interviewId);
      if (cancelled) return;

      if (response.error || !response.data) {
        console.warn('[ActualInterviewPage] resume-state failed:', response.error);
        navigate('/', { replace: true, state: { error: response.error ?? 'Interview not found' } });
        return;
      }

      const resumed = response.data;

      let locale: string | undefined;
      if (resumed.invitation_token) {
        const tokenResponse = await getInterviewByToken(resumed.invitation_token);
        if (tokenResponse.data?.locale) {
          locale = normalizeLocale(tokenResponse.data.locale);
        }
      }

      // Reload of an already-completed interview → return to the prep
      // page (which renders the post-completion view).
      if (resumed.status === InterviewStatus.COMPLETED && resumed.invitation_token) {
        navigate(`/interview/${resumed.invitation_token}`, {
          replace: true,
          state: { fromInterview: true, interviewCompleted: true },
        });
        return;
      }

      navigate(location.pathname, {
        replace: true,
        state: {
          timeRemainingMinutes: resumed.time_remaining_minutes,
          startedAt: resumed.started_at ?? Math.floor(Date.now() / 1000),
          interviewStatus: resumed.status as InterviewStatus,
          talentName: resumed.talent_name ?? undefined,
          jobTitle: resumed.job_title ?? undefined,
          invitationToken: resumed.invitation_token ?? undefined,
          locale,
          // Candidate lost the media stream when the tab closed — must
          // re-grant camera + mic before resuming.
          needsMediaSetup: true,
        },
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [initialState, interviewId, navigate, location.pathname, initialStateCheckDone]);

  // Helper function for navigation after interview ends/expires
  const navigateAfterInterview = useCallback((completed: boolean = false) => {
    const targetPath = initialState?.invitationToken ? `/interview/${initialState.invitationToken}` : '/';
    const navigationState = { fromInterview: true, interviewCompleted: completed };
    console.log(`[ActualInterviewPage] Navigating after interview to: ${targetPath}`, navigationState);
    navigate(targetPath, { replace: true, state: navigationState }); 
  }, [navigate, initialState?.invitationToken]);

  // Handle timer expiration
  const handleTimerExpired = useCallback(async () => {
    if (!isInterviewStarted && currentStatus !== InterviewStatus.IN_PROGRESS) {
      console.log('[ActualInterviewPage] Timer expired check, but interview was never properly started. Skipping automatic end.');
      return;
    }
    
    if (isEnding) {
      console.log('[ActualInterviewPage] Timer expired, but already ending. Skipping.');
      return;
    }
    console.log('[ActualInterviewPage] Interview timer expired. Starting cleanup and navigation.');
    setIsEnding(true); 

    // Stop recording if active
    if (isRecording && interviewId) {
      try {
        await stopQuestionRecording(interviewId);
      } catch (err) {
        console.error('[ActualInterviewPage] Error stopping recording on timer expiry:', err);
      }
    }

    // Disconnect from LiveKit
    await disconnect();
    
    await new Promise(resolve => setTimeout(resolve, 100));

    navigateAfterInterview(true);

    // End interview in background.
    if (interviewId) {
      endInterview(interviewId).catch(err => {
        console.error('[ActualInterviewPage] Error ending interview on timer expiry (background):', err);
      });
    }
  }, [interviewId, disconnect, navigateAfterInterview, isEnding, isInterviewStarted, currentStatus, isRecording]);

  // Handle navigation confirmation
  const handleConfirmNavigation = useCallback(async () => {
    setShowNavigationConfirm(false);
    setPendingNavigation(true);
    
    // Stop recording if active
    if (isRecording && interviewId) {
      try {
        await stopQuestionRecording(interviewId);
      } catch (err) {
        console.error('[ActualInterviewPage] Error stopping recording on navigation:', err);
      }
    }

    // Stop voice detection
    stopVoiceDetection();

    // Stop pre-granted streams
    if (preGrantedStreams?.cameraStream) {
      preGrantedStreams.cameraStream.getTracks().forEach(track => track.stop());
    }
    if (preGrantedStreams?.screenStream) {
      preGrantedStreams.screenStream.getTracks().forEach(track => track.stop());
    }

    // Disconnect from LiveKit
    await disconnect();
    
    try {
      if (interviewId) {
        await endInterview(interviewId);
        console.log('[ActualInterviewPage] Interview ended successfully via navigation confirmation.');
      }
    } catch (error) {
      console.error('[ActualInterviewPage] Error ending the interview via navigation confirmation:', error);
      notify('live.notifications.submit_failed', 'error');
      setPendingNavigation(false);
      return;
    }
    
    navigateAfterInterview(true);
  }, [disconnect, interviewId, navigateAfterInterview, isRecording, stopVoiceDetection, preGrantedStreams]);

  const handleCancelNavigation = useCallback(() => {
    setShowNavigationConfirm(false);
    setPendingNavigation(false);
  }, []);
  
  // Check for time remaining <= 0
  useEffect(() => {
     console.log('[ActualInterviewPage] Timer expiry check effect running.', {
       currentTimeRemaining,
       isEnding,
       initialStateCheckDone,
       isInterviewStarted,
       currentStatus 
     });
    
    if (currentTimeRemaining !== null && currentTimeRemaining <= 0 && !isEnding && initialStateCheckDone && (isInterviewStarted || currentStatus === InterviewStatus.IN_PROGRESS)) {
      console.log('[ActualInterviewPage] Detected expired time remaining in useEffect for an active/in-progress interview. Calling handleTimerExpired.');
      handleTimerExpired();
    } else if (currentTimeRemaining !== null && currentTimeRemaining <= 0 && !isEnding && initialStateCheckDone) {
      console.log('[ActualInterviewPage] Detected expired time remaining, but interview status was SCHEDULED. Timer should not have run yet.');
    }
  }, [currentTimeRemaining, handleTimerExpired, isEnding, initialStateCheckDone, isInterviewStarted, currentStatus]);
  
  // Handle end interview button click — opens the confirmation modal.
  const handleEndInterview = useCallback(() => {
    if (isEnding) {
      console.log('[ActualInterviewPage] End interview clicked, but already ending. Skipping.');
      return;
    }
    setShowEndConfirm(true);
  }, [isEnding]);

  // Runs once the user confirms ending the interview.
  const performEndInterview = useCallback(async () => {
    if (isEnding) return;

    console.log('[ActualInterviewPage] User confirmed ending interview. Starting cleanup and API call.');

    // Stop recording if active
    if (isRecording && interviewId) {
      try {
        await stopQuestionRecording(interviewId);
        setIsRecording(false);
      } catch (err) {
        console.error('[ActualInterviewPage] Error stopping recording on end:', err);
      }
    }

    setIsEnding(true);

    // Disconnect from LiveKit
    await disconnect();

    try {
      if (interviewId) {
        await endInterview(interviewId);
        console.log('[ActualInterviewPage] Interview ended successfully via API.');
        navigateAfterInterview(true);
      } else {
        console.warn('[ActualInterviewPage] No interviewId found, navigating without API call.');
        navigateAfterInterview(true);
      }
    } catch (error) {
      console.error('[ActualInterviewPage] Error ending the interview manually:', error);
      notify('live.notifications.end_failed', 'error');
      setIsEnding(false);
    }
  }, [interviewId, disconnect, navigateAfterInterview, isEnding, isRecording]);
  
  // Handle starting interview (after setup) - POC Pattern
  const handleStartInterview = async () => {
    // Check if audio track exists on the camera stream
    const audioTrack = preGrantedStreams?.cameraStream?.getAudioTracks()[0];
    const hasAudioTrack = !!audioTrack && audioTrack.readyState === 'live';
    
    // Check if pre-granted streams are ready AND audio has been verified AND audio track exists
    if (!preGrantedStreams?.cameraStream || !preGrantedStreams?.screenStream || !isAudioVerified || !hasAudioTrack || isStartingInterview || isInterviewStarted) {
      console.log('[handleStartInterview] Start conditions not met.', {
        hasCameraStream: !!preGrantedStreams?.cameraStream,
        hasScreenStream: !!preGrantedStreams?.screenStream,
        hasAudioTrack,
        audioTrackState: audioTrack?.readyState,
        isAudioVerified,
        isStartingInterview,
        isInterviewStarted,
      });
      
      if (!hasAudioTrack && preGrantedStreams?.cameraStream) {
        notify('live.notifications.audio_unavailable', 'error');
      }
      return;
    }
    
    try {
      setIsStartingInterview(true);
      if (!interviewId) {
        notify('live.notifications.id_missing', 'error');
        setIsStartingInterview(false); 
        return;
      }
      
      // Step 1: Call the endpoint to actually start the interview session
      console.log('[handleStartInterview] Step 1: Starting interview session...');
      const response = await startInterview(interviewId);

      console.log('[handleStartInterview] Response:', response);
      
      if (response.error) {
        notify('live.notifications.start_failed', 'error', { error: response.error });
        setIsStartingInterview(false);
        return;
      }
      
      if (!response.data) {
        notify('live.notifications.start_invalid_response', 'error');
        setIsStartingInterview(false);
        return;
      }

      // Update interview state
      console.log('[handleStartInterview] Step 2: Updating interview state...', response.data);
        setCurrentStartedAt(response.data.started_at);
        setCurrentTimeRemaining(response.data.time_remaining_minutes);
        // Capture language for TTS voice selection
        if (response.data.language) {
          const examLanguage = normalizeExamLanguage(response.data.language);
          setInterviewLanguage(examLanguage);
          console.log(`[handleStartInterview] Interview language: ${examLanguage}`);
        }
        const newStatus = InterviewStatus[response.data.status as keyof typeof InterviewStatus];
        if (newStatus) {
            setCurrentStatus(newStatus);
        } else {
            console.error(`[handleStartInterview] Invalid status received from API: ${response.data.status}`);
        setCurrentStatus(InterviewStatus.IN_PROGRESS);
      }

      // Update LiveKit credentials if provided in response (use type assertion for optional fields)
      const responseData = response.data as any;
      if (responseData.room_name && responseData.livekit_token && responseData.livekit_url) {
        console.log('[handleStartInterview] Updating LiveKit credentials from response');
        setLivekitCredentials({
          roomName: responseData.room_name,
          token: responseData.livekit_token,
          url: responseData.livekit_url,
        });
      }
      
      // Step 3: Connect to LiveKit room
      console.log('[handleStartInterview] Step 3: Connecting to LiveKit room...');
      const connected = await connectToRoom();
      if (!connected) {
        notify('live.notifications.connect_failed', 'error');
        setIsStartingInterview(false);
        return;
      }
      console.log('[handleStartInterview] ✓ Connected to LiveKit room');

      // Give the connection a moment to stabilize
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 4: Publish pre-granted tracks
      console.log('[handleStartInterview] Step 4: Publishing pre-granted tracks...');
      
      // Prepare all tracks for simultaneous publishing
      const publishPromises: Promise<void>[] = [];
      const userMediaStreamName = 'user-camera-mic';
      
      // Prepare camera + audio with synchronized stream (same stream name for A/V sync)
      if (preGrantedStreams.cameraStream) {
        const videoTrack = preGrantedStreams.cameraStream.getVideoTracks()[0];
        const audioTrack = preGrantedStreams.cameraStream.getAudioTracks()[0];
        
        if (videoTrack && audioTrack) {
          // Publish both camera and microphone simultaneously for better synchronization
          // Using the same stream name ensures they are placed in the same MediaStream
          publishPromises.push(
            room.localParticipant.publishTrack(videoTrack, {
              name: 'camera',
              source: Track.Source.Camera,
              stream: userMediaStreamName,
            }).then(() => {
              console.log('[handleStartInterview] ✓ Camera video track published');
            })
          );
          publishPromises.push(
            room.localParticipant.publishTrack(audioTrack, {
              name: 'microphone',
              source: Track.Source.Microphone,
              stream: userMediaStreamName,
            }).then(() => {
              console.log('[handleStartInterview] ✓ Microphone audio track published');
            })
          );
        } else if (videoTrack) {
          publishPromises.push(
            room.localParticipant.publishTrack(videoTrack, {
              name: 'camera',
              source: Track.Source.Camera,
              stream: userMediaStreamName,
            }).then(() => {
              console.log('[handleStartInterview] ✓ Camera video track published');
            })
          );
        }
      }

      // Prepare screen share
      if (preGrantedStreams.screenStream) {
        const screenTrack = preGrantedStreams.screenStream.getVideoTracks()[0];
        
        if (screenTrack) {
          publishPromises.push(
            room.localParticipant.publishTrack(screenTrack, {
              name: 'screen_share',
              source: Track.Source.ScreenShare,
              simulcast: false, // Disable simulcast for faster egress start
            }).then(() => {
              console.log('[handleStartInterview] ✓ Screen share track published (single quality layer)');
            })
          );
        }
      }

      // Publish all tracks simultaneously for better synchronization
      await Promise.all(publishPromises);
      console.log('[handleStartInterview] ✅ All pre-granted tracks published simultaneously');

      // Mark as started
      setIsInterviewStarted(true);
      setSetupComplete(true);
      notify('live.notifications.started_success', 'success');

      // Fetch psychometric sections (if any) in background
      if (interviewId) {
        getPsychometricSections(interviewId).then((res) => {
          if (res.data && res.data.psychometric_sections.length > 0) {
            console.log('[ActualInterviewPage] Psychometric sections loaded:', res.data.psychometric_sections.length);
            setPsychometricSections(res.data.psychometric_sections);
          }
        }).catch((err) => {
          console.warn('[ActualInterviewPage] Could not fetch psychometric sections:', err);
        });
      }
      
    } catch (error) {
      console.error('[ActualInterviewPage] Error starting interview:', error);
      notify('live.notifications.start_retry', 'error');
      setIsStartingInterview(false);
    } finally {
      setIsStartingInterview(false);
    }
  };
  
  // Handle psychometric test completion
  const handlePsychometricComplete = useCallback(async () => {
    console.log('[ActualInterviewPage] Psychometric test complete. Ending interview.');
    setIsEnding(true);
    setInterviewPhase('complete');

    // Now disconnect from LiveKit (kept alive through psychometric phase for video feeds)
    await disconnect();

    try {
      if (interviewId) {
        await endInterview(interviewId);
        console.log('[ActualInterviewPage] Interview ended successfully after psychometric phase.');
        navigateAfterInterview(true);
      } else {
        navigateAfterInterview(true);
      }
    } catch (error) {
      console.error('[ActualInterviewPage] Error ending interview after psychometric:', error);
      navigateAfterInterview(true);
    }
  }, [interviewId, disconnect, navigateAfterInterview]);

  // Handle questions loaded
  const handleQuestionsLoaded = useCallback((questions: QuestionItem[]) => {
    setAllQuestions(questions);
    setTotalQuestions(questions.length);
    console.log('[ActualInterviewPage] Questions loaded:', questions.length);
  }, []);

  // Handle next question
  const handleNextQuestion = useCallback(() => {
    console.log('[ActualInterviewPage] Next question requested from parent');
    // Trigger the next question function from InterviewQuestions
    if (nextQuestionTriggerRef.current) {
      nextQuestionTriggerRef.current();
    }
  }, []);

  // Single source of truth for the spoken/code → psychometric transition.
  // Called by the timer-expiry path AND the manual-click path so the two
  // cannot drift apart again. NEVER calls endInterview() — that happens
  // later in handlePsychometricComplete.
  const advanceToPsychometric = useCallback(async () => {
    if (isRecording && interviewId) {
      try {
        await stopQuestionRecording(interviewId);
        setIsRecording(false);
      } catch (err) {
        console.error('[ActualInterviewPage] Error stopping recording on advance to psychometric:', err);
      }
    }
    if (interviewId) {
      logInterviewQuestionsEnd(interviewId).catch((err) =>
        console.warn('[ActualInterviewPage] Failed to log interview questions end:', err)
      );
    }
    // Mirror the sentinel into the realtime-transcription stream so the
    // backend closes the open Azure session at the same boundary.
    realtimeTranscription.signalEndOfQuestions();
    setInterviewPhase('psychometric');
  }, [interviewId, isRecording, realtimeTranscription]);

  // Manual button handler: candidate clicks "Continue to Personality Test".
  // Must transition to the psychometric phase; must NOT show the
  // end-interview confirmation toast or call endInterview().
  const handleAdvanceToPsychometric = useCallback(async () => {
    if (isEnding) return;
    console.log('[ActualInterviewPage] User clicked advance-to-psychometric.');
    await advanceToPsychometric();
  }, [advanceToPsychometric, isEnding]);

  // Handle last question time up
  const handleLastQuestionTimeUp = useCallback(async () => {
    if (isEnding) {
      console.log('[ActualInterviewPage] Last question time up, but already ending. Skipping.');
      return;
    }

    // If there are psychometric sections, transition (advanceToPsychometric stops recording itself)
    if (psychometricSections.length > 0) {
      console.log('[ActualInterviewPage] Interview questions complete. Transitioning to psychometric phase.');
      await advanceToPsychometric();
      return;
    }

    // No psychometric sections: stop recording, then end interview normally
    if (isRecording && interviewId) {
      try {
        await stopQuestionRecording(interviewId);
        setIsRecording(false);
      } catch (err) {
        console.error('[ActualInterviewPage] Error stopping recording on last question time up:', err);
      }
    }

    console.log('[ActualInterviewPage] Last question time is up. Automatically ending interview.');
    setIsEnding(true);

    // Disconnect from LiveKit
    await disconnect();

    try {
      if (interviewId) {
        await endInterview(interviewId);
        console.log('[ActualInterviewPage] Interview ended successfully due to last question time up.');
        navigateAfterInterview(true);
      } else {
        console.warn('[ActualInterviewPage] No interviewId found, navigating without API call.');
        navigateAfterInterview(true);
      }
    } catch (error) {
      console.error('[ActualInterviewPage] Error ending the interview on last question time up:', error);
      notify('live.notifications.time_completed', 'info');
      // Still navigate even if there's an error
      navigateAfterInterview(true);
    }
  }, [interviewId, disconnect, navigateAfterInterview, isEnding, isRecording, psychometricSections, advanceToPsychometric]);

  // Handle question change - stop/start recording
  const handleQuestionChange = useCallback(async (question: QuestionItem | null) => {
    // Update current question for progress tracking
    setCurrentQuestion(question);

    // Update current question index
    if (question) {
      const index = allQuestions.findIndex(q => q.index === question.index);
      setCurrentQuestionIndex(index);
    } else {
      setCurrentQuestionIndex(-1);
    }

    // Signal the realtime-transcription fallback so the backend can
    // close the previous Azure session and open a fresh one for this
    // question. No-op when the fallback is disabled or the hook hasn't
    // attached yet.
    if (question && typeof question.index === 'number') {
      realtimeTranscription.signalQuestionStart(question.index);
    }

    if (!question) {
      console.log('[ActualInterviewPage] Question is null, skipping recording logic');
      return;
    }

    console.log('[ActualInterviewPage] Question changed:', question);
    
    if (!interviewId) return;

    // If we have a different question, stop previous recording and start new one
    const questionId = String(question.index);
    
    // Set isRecording to false to show "Preparing next question..." state
    // This ensures we don't show the question content until recording actually starts
    setIsRecording(false);
    
    // If we have a different question, stop previous recording
    if (currentQuestionIdRef.current && currentQuestionIdRef.current !== questionId) {
      console.log('[ActualInterviewPage] Stopping recording for previous question');
      try {
        const stopResponse = await stopQuestionRecording(interviewId);
        if (stopResponse.data) {
          console.log('[ActualInterviewPage] Previous recording stopped:', stopResponse.data.message);
        }
      } catch (error) {
        console.error('[ActualInterviewPage] Error stopping previous recording:', error);
      }
    }

    // Start new recording
    console.log('[ActualInterviewPage] Starting recording for question:', questionId);
    try {
      const startResponse = await startQuestionRecording(interviewId, questionId);
      if (startResponse.data) {
        console.log('[ActualInterviewPage] Recording started:', startResponse.data.message);
        setIsRecording(true);
        currentQuestionIdRef.current = questionId;
      } else {
        console.error('[ActualInterviewPage] Failed to start recording:', startResponse.error);
        notify('live.notifications.recording_failed', 'error');
        setIsRecording(false);
      }
    } catch (error) {
      console.error('[ActualInterviewPage] Error starting recording:', error);
      notify('live.notifications.recording_failed', 'error');
      setIsRecording(false);
    }
  }, [interviewId, allQuestions, realtimeTranscription]);

  // Setup phase: Request permissions FIRST (before connecting to room)
  const handleSetupCamera = async () => {
    try {
      console.log('[ActualInterviewPage] Requesting camera + audio permissions...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 15 }
        },
        audio: true
      });

      console.log('[ActualInterviewPage] ✓ Camera + audio access granted');
      
      // Store stream for later publishing
      setPreGrantedStreams(prev => ({
        cameraStream: stream,
        screenStream: prev?.screenStream || null,
      }));
      
      // Attach to video element for preview
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        cameraVideoRef.current.muted = true;
        await cameraVideoRef.current.play().catch(e => console.warn('Could not play camera preview:', e));
      }
      
      // Start voice activity detection
      startVoiceDetection(stream);
      
      notify('live.notifications.camera_enabled', 'success');
    } catch (error) {
      console.error('[ActualInterviewPage] Error enabling camera:', error);
      notify('live.notifications.camera_failed', 'error');
    }
  };

  const handleSetupScreen = async () => {
    try {
      console.log('[ActualInterviewPage] Requesting screen share permissions...');
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'monitor', // Request full screen only
          frameRate: { ideal: 10 }
        },
        audio: false
      });

      // Validate that a full screen (monitor) was selected, not a tab/window
      const videoTrack = stream.getVideoTracks()[0];
      const settings = videoTrack.getSettings();
      
      console.log('[ActualInterviewPage] Screen share settings:', settings);
      
      // Check if displaySurface is 'monitor' (full screen)
      if (settings.displaySurface && settings.displaySurface !== 'monitor') {
        // User selected a window or tab instead of full screen
        stream.getTracks().forEach(track => track.stop());
        notify('live.notifications.screen_not_full', 'error');
        console.warn('[ActualInterviewPage] User selected window/tab instead of full screen');
        return;
      }

      console.log('[ActualInterviewPage] ✓ Full screen share access granted');
      
      // Store stream for later publishing
      setPreGrantedStreams(prev => ({
        cameraStream: prev?.cameraStream || null,
        screenStream: stream,
      }));
      
      // Attach to video element for preview
      if (screenVideoRef.current) {
        screenVideoRef.current.srcObject = stream;
        await screenVideoRef.current.play().catch(e => console.warn('Could not play screen preview:', e));
      }
      
      // Add event listener to detect when user stops sharing
      videoTrack.addEventListener('ended', () => {
        console.log('[ActualInterviewPage] Screen sharing stopped by user');
        setPreGrantedStreams(prev => ({
          cameraStream: prev?.cameraStream || null,
          screenStream: null,
        }));
        if (isInterviewStarted) {
          notify('live.notifications.screen_stopped', 'error');
        }
      });
      
      notify('live.notifications.screen_enabled', 'success');
    } catch (error) {
      console.error('[ActualInterviewPage] Error enabling screen sharing:', error);
      if ((error as Error).name === 'NotAllowedError') {
        notify('live.notifications.screen_denied', 'error');
      } else {
        notify('live.notifications.screen_failed', 'error');
      }
    }
  };

  // Handler to stop camera
  const handleStopCamera = () => {
    if (preGrantedStreams?.cameraStream) {
      console.log('[ActualInterviewPage] Stopping camera...');
      preGrantedStreams.cameraStream.getTracks().forEach(track => track.stop());
      setPreGrantedStreams(prev => ({
        cameraStream: null,
        screenStream: prev?.screenStream || null,
      }));
      // Reset audio verification when camera (with mic) is stopped
      stopVoiceDetection();
      setIsAudioVerified(false);
      notify('live.notifications.camera_stopped', 'info');
    }
  };

  // Handler to stop screen sharing
  const handleStopScreen = () => {
    if (preGrantedStreams?.screenStream) {
      console.log('[ActualInterviewPage] Stopping screen sharing...');
      preGrantedStreams.screenStream.getTracks().forEach(track => track.stop());
      setPreGrantedStreams(prev => ({
        cameraStream: prev?.cameraStream || null,
        screenStream: null,
      }));
      notify('live.notifications.screen_sharing_stopped', 'info');
    }
  };

  // Load LiveKit credentials from initial state or fetch them
  useEffect(() => {
    const loadCredentials = async () => {
      if (!initialStateCheckDone || livekitCredentials || !interviewId) {
        return;
      }

      // Check if credentials are in initial state first
      if (initialState?.livekit_url && initialState?.livekit_token && initialState?.room_name) {
        console.log('[ActualInterviewPage] Loading LiveKit credentials from initial state');
        setLivekitCredentials({
          roomName: initialState.room_name,
          token: initialState.livekit_token,
          url: initialState.livekit_url,
        });
        return;
      }

      // Fallback: Fetch credentials from API if not in initial state
      console.log('[ActualInterviewPage] Credentials not in initial state, fetching from API...');
      try {
        const response = await getLiveKitToken(interviewId);
        if (response.error) {
          console.error('[ActualInterviewPage] Failed to fetch LiveKit credentials:', response.error);
          notify('live.notifications.credentials_failed', 'error');
          return;
        }

        if (response.data) {
          console.log('[ActualInterviewPage] ✓ LiveKit credentials fetched from API');
          setLivekitCredentials({
            roomName: response.data.room_name,
            token: response.data.livekit_token,
            url: response.data.livekit_url,
          });
        }
      } catch (error) {
        console.error('[ActualInterviewPage] Error fetching LiveKit credentials:', error);
        notify('live.notifications.credentials_failed', 'error');
      }
    };

    loadCredentials();
  }, [initialStateCheckDone, initialState, livekitCredentials, interviewId]);

  // Feed status checking removed in split-screen layout

  // Render setup instructions before interview starts
  const renderSetupInstructions = () => {
    const hasCameraStream = !!preGrantedStreams?.cameraStream;
    const hasScreenStream = !!preGrantedStreams?.screenStream;
    const audioTrack = preGrantedStreams?.cameraStream?.getAudioTracks()[0];
    const hasAudioTrack = !!audioTrack && audioTrack.readyState === 'live';
    const canStartInterview = hasCameraStream && hasScreenStream && isAudioVerified && hasAudioTrack && isChromeBrowser;

    const StepIcon = ({ done, error: hasError, step }: { done: boolean; error?: boolean; step: number }) => (
      <span className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold ${
        done ? 'bg-green text-white' : hasError ? 'bg-red text-white' : 'bg-gray-100 text-dark-invirtus/50 border border-gray-200'
      }`}>
        {done ? <Check className="w-3.5 h-3.5" /> : hasError ? <X className="w-3.5 h-3.5" /> : step}
      </span>
    );

    return (
      <div className="space-y-6">
        {/* Setup Checklist */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold text-dark-invirtus font-heading tracking-tight mb-2">{t('live.setup.title')}</h2>
          <p className="text-sm text-dark-invirtus/60 font-body mb-6">{t('live.setup.subtitle')}</p>

          <div className="space-y-1">
            {/* Step 1: Chrome */}
            <div className="flex items-start gap-3 py-3 px-1">
              <StepIcon done={isChromeBrowser} error={!isChromeBrowser} step={1} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Chrome className="w-4 h-4 text-dark-invirtus/40" />
                  <p className="text-sm font-bold text-dark-invirtus font-heading">{t('live.setup.chrome_title')}</p>
                </div>
                <p className="text-xs text-dark-invirtus/50 font-body mt-0.5">
                  {isChromeBrowser ? (
                    <span className="text-green">{t('live.setup.chrome_detected')}</span>
                  ) : (
                    <span className="text-red">
                      {t('live.setup.chrome_required')}{' '}
                      <a href="https://www.google.com/intl/fr/chrome/" target="_blank" rel="noopener noreferrer" className="underline hover:text-dark-invirtus">
                        {t('live.setup.download_chrome')}
                      </a>
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Step 2: Camera */}
            <div className="flex items-start gap-3 py-3 px-1">
              <StepIcon done={hasCameraStream} step={2} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Camera className="w-4 h-4 text-dark-invirtus/40" />
                  <p className="text-sm font-bold text-dark-invirtus font-heading">{t('live.setup.camera_title')}</p>
                </div>
                <p className="text-xs text-dark-invirtus/50 font-body mt-0.5">{t('live.setup.camera_desc')}</p>
              </div>
            </div>

            {/* Step 3: Microphone */}
            <div className="flex items-start gap-3 py-3 px-1">
              <StepIcon done={isAudioVerified && hasAudioTrack} error={hasCameraStream && !hasAudioTrack} step={3} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Mic className="w-4 h-4 text-dark-invirtus/40" />
                  <p className="text-sm font-bold text-dark-invirtus font-heading">{t('live.setup.mic_title')}</p>
                </div>
                <p className="text-xs font-body mt-0.5">
                  {hasCameraStream && !hasAudioTrack ? (
                    <span className="text-red font-medium">{t('live.setup.mic_no_device')}</span>
                  ) : hasCameraStream && !isAudioVerified ? (
                    <span className="text-amber-600 font-medium">{t('live.setup.mic_speak_now')}</span>
                  ) : isAudioVerified && hasAudioTrack ? (
                    <span className="text-green">{t('live.setup.mic_verified')}</span>
                  ) : (
                    <span className="text-dark-invirtus/50">{t('live.setup.mic_enable_first')}</span>
                  )}
                </p>
                {/* Inline mic indicator - only show when not yet verified */}
                {hasCameraStream && hasAudioTrack && !isAudioVerified && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    {isVoiceDetected ? (
                      <Mic className="w-3 h-3 text-green" />
                    ) : (
                      <MicOff className="w-3 h-3 text-gray-300" />
                    )}
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${isVoiceDetected ? 'bg-green' : 'bg-gray-300'}`} />
                    <span className="text-[11px] text-dark-invirtus/40 font-body">
                      {isVoiceDetected ? t('live.setup.voice_detected') : t('live.setup.voice_not_detected')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Step 4: Screen Share */}
            <div className="flex items-start gap-3 py-3 px-1">
              <StepIcon done={hasScreenStream} step={4} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-dark-invirtus/40" />
                  <p className="text-sm font-bold text-dark-invirtus font-heading">{t('live.setup.screen_title')}</p>
                </div>
                <p className="text-xs text-dark-invirtus/50 font-body mt-0.5">{t('live.setup.screen_desc')}</p>
              </div>
            </div>
          </div>

          {/* Audio error banner */}
          {hasCameraStream && !hasAudioTrack && (
            <div className="mt-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold font-heading">{t('live.setup.audio_banner_title')}</p>
                <p className="text-xs mt-0.5 font-body">{t('live.setup.audio_banner_desc')}</p>
              </div>
            </div>
          )}

          {/* Media Controls + Start */}
          <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                onClick={hasCameraStream ? handleStopCamera : handleSetupCamera}
                label={hasCameraStream ? t('live.setup.camera_on') : t('live.setup.enable_camera')}
                size="sm"
                icon={hasCameraStream ? <Camera/> : <CameraOff/>}
                {...(hasCameraStream
                  ? { type: 'contained' as const, intent: 'success' as const }
                  : { type: 'outlined' as const, intent: 'muted' as const, dashed: true })}
              />
              <Button
                onClick={hasScreenStream ? handleStopScreen : handleSetupScreen}
                label={hasScreenStream ? t('live.setup.screen_shared') : t('live.setup.share_screen')}
                size="sm"
                icon={hasScreenStream ? <Monitor/> : <MonitorOff/>}
                {...(hasScreenStream
                  ? { type: 'contained' as const, intent: 'success' as const }
                  : { type: 'outlined' as const, intent: 'muted' as const, dashed: true })}
              />
            </div>
            <Button
              onClick={handleStartInterview}
              disabled={!canStartInterview || isStartingInterview}
              label={isStartingInterview ? t('live.setup.starting') : t('live.setup.start_interview')}
              type="contained"
              intent="secondary"
              loading={isStartingInterview}
            />
          </div>
        </div>
      </div>
    );
  };
  
  // Loading/Error states
  if (!initialStateCheckDone) {
    console.log('[ActualInterviewPage] Rendering Loading: Initial state check not done.');
    return <Loading />;
  }
  
  if (!currentStatus || currentStartedAt === null || currentTimeRemaining === null) {
     console.error('[ActualInterviewPage] Rendering Error: Crucial state missing after initial check completed.');
     return <Loading />; 
  }
  
  if (isEnding) {
    console.log('[ActualInterviewPage] Rendering Loading: Ending interview process.');
    return <Loading />;
  }
  
  return (
    <RoomContext.Provider value={room}>
    <div
      className={`${(isInterviewStarted && setupComplete) || interviewPhase === 'psychometric' ? 'h-screen' : 'min-h-screen'} bg-white flex flex-col relative`}
      onCopy={handleCopyProtection}
      onCut={handleCutProtection}
      onContextMenu={handleContextMenuProtection}
      style={isInterviewStarted && setupComplete ? copyProtectedStyle : undefined}
    >
        {/* Setup Section - Only show when not in active interview split-screen mode */}
        {!(isInterviewStarted && setupComplete) && (
          <div className="min-h-screen bg-[#F8F8F8]">
            {/* Compact Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
              <div className="max-w-5xl mx-auto flex items-center gap-3">
                <img src={brandLogoSrc} alt="Brand logo" className="h-10 w-10 object-contain" />
                <div>
                  <h1 className="text-lg font-bold text-dark-invirtus font-heading tracking-tight leading-tight">
                    {initialState?.talentName
                      ? t('live.page_title', { name: initialState.talentName })
                      : t('live.page_title_fallback')}
                    {initialState?.jobTitle && <span className="text-dark-invirtus/40 font-medium"> — {initialState.jobTitle}</span>}
                  </h1>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="relative max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
              {/* Previews - absolute to far right, top-aligned with content */}
              <div className="absolute right-[-12rem] top-8 hidden xl:flex flex-col gap-3 w-44">
                  {/* Camera Preview */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-3 py-1.5 border-b border-gray-100 flex items-center gap-1.5">
                      <Camera className="w-3 h-3 text-dark-invirtus/40" />
                      <span className="text-[10px] font-bold text-dark-invirtus font-heading uppercase tracking-wider">{t('live.preview.camera')}</span>
                      {preGrantedStreams?.cameraStream && (
                        <span className="ml-auto w-1.5 h-1.5 bg-green rounded-full" />
                      )}
                    </div>
                    <div className="aspect-video bg-dark-invirtus relative">
                      <video ref={cameraVideoRef} autoPlay playsInline className="w-full h-full object-contain" />
                      {!preGrantedStreams?.cameraStream && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <CameraOff className="w-5 h-5 text-white/30 mx-auto mb-1" />
                            <Button
                              onClick={handleSetupCamera}
                              label={t('live.preview.enable')}
                              type="contained"
                              intent="primary"
                              size="xs"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Screen Preview */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-3 py-1.5 border-b border-gray-100 flex items-center gap-1.5">
                      <Monitor className="w-3 h-3 text-dark-invirtus/40" />
                      <span className="text-[10px] font-bold text-dark-invirtus font-heading uppercase tracking-wider">{t('live.preview.screen')}</span>
                      {preGrantedStreams?.screenStream && (
                        <span className="ml-auto w-1.5 h-1.5 bg-green rounded-full" />
                      )}
                    </div>
                    <div className="aspect-video bg-dark-invirtus relative">
                      <video ref={screenVideoRef} autoPlay playsInline className="w-full h-full object-contain" />
                      {!preGrantedStreams?.screenStream && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <MonitorOff className="w-5 h-5 text-white/30 mx-auto mb-1" />
                            <Button
                              onClick={handleSetupScreen}
                              label={t('live.preview.share')}
                              type="contained"
                              intent="primary"
                              size="xs"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              {/* Setup Checklist - full width */}
              {renderSetupInstructions()}
            </div>
          </div>
        )}

        {/* Main interview content - Split Screen Layout (interview + psychometric phases) */}
        {isInterviewStarted && setupComplete && (
          <div className="flex-1 flex overflow-hidden">
            {/* Left Side - Branding + Camera/Screen tiles (unchanged across phases) */}
            <div className="w-1/2 bg-gradient-to-br from-purple/10 to-light-purple flex flex-col">
              {/* Hidden video elements for LiveKit (still needed for recording) */}
              <video
                ref={cameraVideoRef}
                autoPlay
                playsInline
                muted
                className="hidden"
              />
              <video
                ref={screenVideoRef}
                autoPlay
                playsInline
                muted
                className="hidden"
              />

              {/* Top Section - Invirtus Branding (75% height) */}
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center space-y-6">
                  <div
                    className={`w-60 h-60 mx-auto flex items-center justify-center ${
                      isCustomBrandLogo
                        ? ''
                        : 'rounded-full bg-white shadow-[0_20px_55px_rgba(0,0,0,0.12)]'
                    }`}
                  >
                    <img
                      src={brandLogoSrc}
                      alt="Brand logo"
                      className="w-44 h-44 object-contain"
                    />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-2xl font-bold text-dark-invirtus font-heading">
                      {interviewPhase === 'psychometric'
                        ? t('live.during.personality_assessment')
                        : t('live.during.in_progress')}
                    </h2>
                    <p className="text-base text-gray-600 font-body">
                      {initialState?.talentName && `${initialState.talentName} - `}
                      {initialState?.jobTitle || t('live.during.technical_interview_fallback')}
                    </p>
                  </div>

                  {/* Recording Status / No Recording indicator */}
                  {interviewPhase === 'psychometric' ? (
                    <div className="flex items-center justify-center gap-2 text-green-600 mt-6">
                      <span className="w-2.5 h-2.5 bg-green-500 rounded-full"></span>
                      <span className="text-sm font-body text-gray-400">{t('live.during.no_recording')}</span>
                    </div>
                  ) : isRecording ? (
                    <div className="flex items-center justify-center gap-2 text-red-600 mt-6">
                      <span className="w-3 h-3 bg-red-600 rounded-full animate-pulse"></span>
                      <span className="font-medium font-heading">{t('live.during.recording')}</span>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Bottom Section - Participant Tiles Horizontal (25% height) */}
              <div className="h-48 border-t border-white/20 p-4">
                <ParticipantTilesHorizontalComponent
                  isVoiceDetected={isVoiceDetected}
                />
              </div>
            </div>

            {/* Right Side - Interview Questions or Psychometric Test */}
            <div className="w-1/2 bg-white flex flex-col overflow-hidden">
              {/* Progress Stepper at top (shared across phases) */}
              <div>
                <InterviewProgressStepper
                  currentQuestionIndex={currentQuestion?.index ?? -1}
                  totalQuestions={totalQuestions}
                  questions={allQuestions}
                  hasPsychometricStep={psychometricSections.length > 0}
                  psychometricActive={interviewPhase === 'psychometric'}
                />
              </div>

              {/* Interview phase: questions + navigation */}
              {interviewPhase !== 'psychometric' && (
                <>
                  {/* TTS unavailable warning */}
                  {ttsError && (
                    <div className="mx-4 mb-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                      {ttsError}
                    </div>
                  )}

                  <div className="flex-1 flex flex-col overflow-hidden">
                    <InterviewQuestions
                      interviewId={interviewId!}
                      startTime={currentStartedAt}
                      isInterviewStarted={isInterviewStarted}
                      mediaStatus={{
                        isAudioEnabled: true,
                        isVideoEnabled: true,
                        isScreenSharingEnabled: true,
                      }}
                      onActiveQuestionChange={handleQuestionChange}
                      onQuestionsLoaded={handleQuestionsLoaded}
                      onLastQuestionTimeUp={handleLastQuestionTimeUp}
                      singleQuestionMode={true}
                      nextQuestionTriggerRef={nextQuestionTriggerRef}
                      isRecording={isRecording}
                      speak={speak}
                      stopTTS={stopTTS}
                      isTTSPlaying={isTTSPlaying}
                      isMuted={isMuted}
                      onToggleMute={() => setIsMuted(!isMuted)}
                    />
                  </div>
                  {/* Bottom Action Button - Next Question or End Interview */}
                  <div className="flex-shrink-0 p-4 pb-2 border-t border-gray-200 bg-gray-50">
                    {currentQuestionIndex < totalQuestions - 1 ? (
                      <div className="flex flex-col items-center gap-1 w-full">
                        <div className="w-full [&>div]:w-full">
                          <Button
                            onClick={handleNextQuestion}
                            label={t('live.actions.next_question')}
                            type="contained"
                            intent="primary"
                            disabled={isEnding || !isRecording}
                            className="w-full"
                          />
                        </div>
                        <Button
                          onClick={handleEndInterview}
                          label={t('live.actions.end_interview')}
                          type="text"
                          intent="danger"
                          size="sm"
                          disabled={isEnding}
                          className="w-full"
                        />
                      </div>
                    ) : (
                      <div className="w-full [&>div]:w-full pb-2">
                        <Button
                          onClick={
                            endOfQuestionsAction(psychometricSections.length) === 'advance-to-psychometric'
                              ? handleAdvanceToPsychometric
                              : handleEndInterview
                          }
                          label={t(endOfQuestionsButtonLabelKey(psychometricSections.length, isEnding))}
                          type="contained"
                          intent="primary"
                          disabled={isEnding}
                          loading={isEnding}
                          className="w-full"
                          id={endOfQuestionsButtonId(psychometricSections.length)}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Psychometric phase: test fills the right panel */}
              {interviewPhase === 'psychometric' && psychometricSections.length > 0 && (
                <div className="flex-1 overflow-hidden">
                  <PsychometricTest
                    interviewId={interviewId!}
                    psychometricSections={psychometricSections}
                    onComplete={handlePsychometricComplete}
                    onEndInterview={handlePsychometricComplete}
                    interviewLanguage={interviewLanguage}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation Confirmation Modal */}
        <ExitInterviewConfirmModal
          open={showNavigationConfirm}
          loading={pendingNavigation}
          enabled={exitConfirmationEnabled}
          onCancel={handleCancelNavigation}
          onConfirm={handleConfirmNavigation}
        />

        {/* End Interview confirmation */}
        <ConfirmationDialog
          isOpen={showEndConfirm}
          onClose={() => setShowEndConfirm(false)}
          title={t('live.end_dialog.title')}
          message={t('live.end_dialog.message')}
          buttons={[
            {
              label: t('live.actions.cancel'),
              onClick: () => setShowEndConfirm(false),
              type: 'outlined',
              intent: 'secondary',
            },
            {
              label: t('live.actions.end_submit_confirm'),
              onClick: () => {
                setShowEndConfirm(false);
                performEndInterview();
              },
              intent: 'danger',
            },
          ]}
        />
      </div>
    </RoomContext.Provider>
  );
}; 
